import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { getAuth, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import firebase from 'firebase/compat/app';
import { BsModalService } from 'ngx-bootstrap';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import { RollbarService } from '../rollbar/rollbar.service';

import { Auth } from './auth.types';

const mutedSigninErrors: string[] = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'];

// ###A
export interface SessionState {
  isConnected: boolean;
  isSessionActive: boolean;
  isFacebookProvided: boolean;
  access_token?: string;
}

// ###A
export interface LogoutOptions {
  bypassSessionEnd?: boolean;
  useRedir?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private afAuth: AngularFireAuth,
    private router: Router,
    private injector: Injector,
    private httpClient: HttpClient,
    private modalService: BsModalService
  ) {
    this.getAuth()
      .pipe(first((auth: Auth) => !!(auth && auth.uid)))
      .subscribe((auth: Auth) => {
        const rollbar = this.injector.get(RollbarService);
        rollbar.configure({
          payload: {
            person: {
              id: auth.uid
            }
          }
        });
      });
  }

  getAuth(): Observable<Auth> {
    return this.afAuth.authState;
  }

  // Angular FireAuth Auth utils

  loginWithCredentials({ email, password }: { email: string; password: string }) {
    return firebase.auth().signInWithEmailAndPassword(email, password); // .catch does not work when calling signInWithEmailAndPassword from AngularFireAuth
  }

  // Angular FireAuth User utils

  getCurrentUser() {
    return getAuth().currentUser as Auth;
  }

  // Other Utils

  signinWithGoogle(): void {
    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/userinfo.email'); // Important ! This scope will guarantee the presence of the email address after login
    provider.setCustomParameters({ prompt: 'select_account' });
    this.afAuth.signInWithPopup(provider).catch((error: any) => {
      if (!mutedSigninErrors.includes(error.code)) console.error(error);
    });
  }

  signinWithMicrosoft(): void {
    const provider = new OAuthProvider('microsoft.com');
    provider.addScope('openid');
    provider.addScope('profile');
    provider.addScope('email');
    provider.addScope('User.Read');
    this.afAuth.signInWithPopup(provider).catch((error: any) => {
      if (!mutedSigninErrors.includes(error.code)) console.error(error);
    });
  }

  loginWithToken(token: string) {
    return this.afAuth.signInWithCustomToken(token);
  }

  logout(options: LogoutOptions = {}): Promise<void> {
    // removing support bot if signing out
    if (environment.useSupportBot && window['ideta_bot_action']) window['ideta_bot_action']('remove');
    return this.afAuth
      .signOut()
      .then(() => (!options.bypassSessionEnd ? this.endSession() : Promise.resolve()))
      .then(() => {
        this.navigateToLogin(options.useRedir);
      })
      .catch((error: any) => {
        console.error(error);
      });
  }

  checkSession(): Observable<SessionState> {
    const res: SessionState = { isConnected: false, isSessionActive: false, isFacebookProvided: true };
    return this.getAuth().pipe(
      switchMap(auth => {
        res.isConnected = auth && !auth.isAnonymous;
        res.isFacebookProvided = res.isConnected && auth.providerData.length === 1 && auth.providerData[0].providerId === 'facebook.com';
        return environment.localLogin
          ? of({ access_token: res.isConnected ? 'granted' : null })
          : this.httpClient.get<{ access_token: string }>(`${environment.backendUrl}/user/auth/check-session?auth=${res.isConnected}&product=inbox`, {
              withCredentials: true
            });
      }),
      catchError(() => of({ access_token: null })),
      map(check => {
        res.isSessionActive = !!check.access_token;
        res.access_token = check.access_token;
        return res;
      })
    );
  }

  navigateToLogin(useRedir?: boolean) {
    if (environment.localLogin) {
      this.router.navigate(['/login']);
    } else {
      const params = useRedir ? `?rd=${window.location.href}` : '';
      window.location.href = `${environment.products.connect}/login${params}`;
    }
  }

  closeUnexpectedPopups(): void {
    for (let i = 1; i <= this.modalService.getModalsCount(); i++) {
      this.modalService.hide(i);
    }
  }

  private endSession(): Promise<any> {
    if (environment.localLogin) return Promise.resolve();
    return lastValueFrom(this.httpClient.post(`${environment.backendUrl}/user/auth/end-session`, null, { withCredentials: true }));
  }
}
